// ============================================================
// _ViewShell — shared header chrome for every Platform Control
// view. Each view owns its own body; the wrapper just keeps the
// kicker + title + sublabel rhythm consistent so the surface
// reads as one product, not 14 stitched pages.
// ============================================================

import type { ReactNode } from 'react'
import { SHELL_TOKENS, text } from '../../../shell/styles'

interface ViewShellProps {
  kicker: string
  title: string
  description?: string
  actions?: ReactNode
  children: ReactNode
}

export default function ViewShell({
  kicker,
  title,
  description,
  actions,
  children,
}: ViewShellProps) {
  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          marginBottom: 18,
          gap: 16,
        }}
      >
        <div>
          <div
            style={{
              ...text.kicker,
              fontSize: 9,
              letterSpacing: '0.26em',
              color: SHELL_TOKENS.inkFaint,
              marginBottom: 6,
            }}
          >
            {kicker}
          </div>
          <div
            style={{
              fontSize: 24,
              fontWeight: 800,
              color: SHELL_TOKENS.ink,
              fontFamily: 'var(--font-brand)',
              letterSpacing: '-0.01em',
            }}
          >
            {title}
          </div>
          {description && (
            <div
              style={{
                fontSize: 12,
                color: SHELL_TOKENS.inkMuted,
                marginTop: 6,
                fontFamily: 'var(--font-mono)',
                maxWidth: 640,
              }}
            >
              {description}
            </div>
          )}
        </div>
        {actions}
      </div>
      {children}
    </div>
  )
}

export function Section({
  label,
  children,
}: {
  label: string
  children: ReactNode
}) {
  return (
    <section style={{ marginBottom: 22 }}>
      <div
        style={{
          ...text.kicker,
          fontSize: 8,
          letterSpacing: '0.22em',
          color: SHELL_TOKENS.inkWhisper,
          marginBottom: 8,
        }}
      >
        {label}
      </div>
      {children}
    </section>
  )
}

export function Card({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        padding: '14px 16px',
        borderRadius: 10,
        background: SHELL_TOKENS.surface,
        border: `1px solid ${SHELL_TOKENS.border}`,
      }}
    >
      {children}
    </div>
  )
}

export function Row({
  label,
  value,
  hint,
}: {
  label: string
  value: ReactNode
  hint?: string
}) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '10px 0',
        borderBottom: `1px solid ${SHELL_TOKENS.border}`,
        gap: 12,
      }}
    >
      <div>
        <div style={{ fontSize: 12, color: SHELL_TOKENS.inkDim, fontWeight: 600 }}>
          {label}
        </div>
        {hint && (
          <div
            style={{
              fontSize: 10,
              color: SHELL_TOKENS.inkMuted,
              fontFamily: 'var(--font-mono)',
              marginTop: 2,
            }}
          >
            {hint}
          </div>
        )}
      </div>
      <div
        style={{
          fontSize: 12,
          color: SHELL_TOKENS.ink,
          fontFamily: 'var(--font-mono)',
          textAlign: 'right',
        }}
      >
        {value}
      </div>
    </div>
  )
}

export function ActionButton({
  children,
  onClick,
  disabled,
  variant = 'default',
  title,
}: {
  children: ReactNode
  onClick?: () => void
  disabled?: boolean
  variant?: 'default' | 'primary' | 'danger'
  title?: string
}) {
  const bg =
    variant === 'primary'
      ? 'linear-gradient(135deg, rgba(109,40,217,0.25), rgba(109,40,217,0.15))'
      : variant === 'danger'
        ? 'linear-gradient(135deg, rgba(220,38,38,0.15), rgba(220,38,38,0.08))'
        : 'linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.03))'
  const border =
    variant === 'primary' ? 'rgba(109,40,217,0.4)'
    : variant === 'danger' ? 'rgba(220,38,38,0.35)'
    : 'rgba(255,255,255,0.12)'
  const color =
    variant === 'primary' ? '#a78bfa'
    : variant === 'danger' ? '#f87171'
    : '#c0c8d2'

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        padding: '12px 20px',
        borderRadius: 10,
        border: `1px solid ${border}`,
        background: bg,
        color: disabled ? SHELL_TOKENS.inkMuted : color,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        fontFamily: 'var(--font-mono)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.45 : 1,
        transition: 'all 0.2s ease',
        boxShadow: variant === 'primary' ? '0 2px 12px rgba(109,40,217,0.2)' : 'none',
      }}
    >
      {children}
    </button>
  )
}

export function ActionRow({ children }: { children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
      {children}
    </div>
  )
}
